import api, { adminApiPrefix } from './Api';
import { PartnerPlatform } from '../interfaces/PartnerPlatform/PartnerPlatform';
import {
  PartnerPlatformSyncJob,
  partnerPlatformSyncJobFromResponse,
} from '../interfaces/PartnerPlatform/PartnerPlatformSyncJob';
import { getStoredToken, isTokenValid, removeToken } from '../utils/Auth';

const partnerPlatformSyncJobAdminApiPrefix = `${adminApiPrefix}/partner-platform-sync-jobs`;

type StreamPartnerPlatformSyncJobOptions = {
  onMessage: (syncJob: PartnerPlatformSyncJob) => void;
  onError?: (message: string) => void;
};

type ServerSentEvent = {
  event: string;
  data: string;
};

const getPartnerPlatformSyncJobStreamUrl = (id: number): string => {
  const path = `${partnerPlatformSyncJobAdminApiPrefix}/${id}/stream`;
  const baseUrl = api.defaults.baseURL;

  if (!baseUrl) {
    return path;
  }

  return `${baseUrl}${path}`;
};

const parseServerSentEvent = (rawEvent: string): ServerSentEvent => {
  const lines = rawEvent.split(/\r?\n/);
  const eventLines = lines.filter((line) => line.startsWith('event:'));
  const dataLines = lines.filter((line) => line.startsWith('data:'));

  return {
    event: eventLines[0]?.replace(/^event:\s?/, '') || 'message',
    data: dataLines.map((line) => line.replace(/^data:\s?/, '')).join('\n'),
  };
};

export const streamPartnerPlatformSyncJob = (
  id: number,
  { onMessage, onError }: StreamPartnerPlatformSyncJobOptions,
): (() => void) => {
  const controller = new AbortController();

  const stream = async () => {
    const headers = new Headers({
      Accept: 'text/event-stream',
    });

    const token = getStoredToken();
    if (token) {
      if (isTokenValid()) {
        headers.set('Authorization', `Bearer ${token}`);
      } else {
        removeToken();
      }
    }

    const response = await fetch(getPartnerPlatformSyncJobStreamUrl(id), {
      headers,
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Sync job stream failed with status ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Sync job stream response is empty');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let isReading = true;

    while (isReading) {
      // Stream chunks must be read sequentially from the same reader.
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();

      if (done) {
        isReading = false;
      } else {
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split(/\r?\n\r?\n/);
        buffer = events.pop() ?? '';

        events.forEach((rawEvent) => {
          const { event, data } = parseServerSentEvent(rawEvent);

          if (!data) {
            return;
          }

          if (event === 'error') {
            onError?.(data);
            return;
          }

          onMessage(partnerPlatformSyncJobFromResponse(JSON.parse(data)));
        });
      }
    }
  };

  stream().catch((err) => {
    if (!controller.signal.aborted) {
      onError?.(err.message);
    }
  });

  return () => controller.abort();
};

export const startPartnerPlatformSyncJob = async (
  partnerPlatform: PartnerPlatform,
): Promise<PartnerPlatformSyncJob> => {
  const response = await api.post(
    `${partnerPlatformSyncJobAdminApiPrefix}/${partnerPlatform}`,
  );

  return partnerPlatformSyncJobFromResponse(response.data?.data);
};

export const getActivePartnerPlatformSyncJobsByPlatform = async (
  platform: PartnerPlatform,
): Promise<PartnerPlatformSyncJob[]> => {
  const response = await api.get(
    `${partnerPlatformSyncJobAdminApiPrefix}/active/${platform}`,
  );

  return (response.data?.data ?? []).map(partnerPlatformSyncJobFromResponse);
};
